
import { EstablishmentTooltipDetails } from "../MarkerTooltip/EstablishmentTooltipDetails";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { useEstablishmentCompaniesInfiniteQuery } from "@/features/establishments/hooks/useEstablishmentCompaniesInfiniteQuery";
import React, { useCallback, useEffect, useRef } from "react";
import EstablishmentMapFilters from "../MapFilters";
import { LoadingEstablishmentTooltipDetails } from "../MarkerTooltip/LoadingTooltipDetails";

interface EstablishmentMapCardListProps {
  setSearch: (value: string) => void;
  setFilterBounds?: (bounds: { top_left_lat: number; top_left_lng: number; bottom_right_lat: number; bottom_right_lng: number; }) => void;
}

const EstablishmentMapCardList = ({ setSearch, setFilterBounds }: EstablishmentMapCardListProps) => {
  const { filterParams } = useFilterParams();

  const {
    data: establishments,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useEstablishmentCompaniesInfiniteQuery({
    filters: {
      search: filterParams.search,
      cityId: filterParams.city?.value,
      stateId: filterParams.state?.value,
      productCategoryId: filterParams.product?.value,
      status: 'enabled',
      commercialKind: 'available_to_everyone',
      caModuleEnabled: true,
    },
  });

  const isInitialLoading = status === 'pending';

  const observerRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;

    if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    });

    if (observerRef.current) observer.observe(observerRef.current);

    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current);
    };
  }, [handleObserver]);

  return (
    <div className="bg-map-search-75 backdrop-blur h-screen overflow-hidden w-[411px] z-20 absolute top-0 left-[-1px] p-6">
      <div className="flex flex-col gap-4 mt-[4.5rem]">
        <EstablishmentMapFilters
          setSearch={setSearch}
        />

        <div className="flex flex-col gap-3 overflow-auto h-[calc(100vh-15rem)]">
          {isInitialLoading && new Array(10).fill(null).map((_, index) => (
            <LoadingEstablishmentTooltipDetails
              key={`establishment-card-skeleton-${index}`}
              cardList
            />
          ))}

          {!isInitialLoading && (establishments?.pages)?.map((page, pageIndex) => (
            <React.Fragment key={`establishment-card-list-page-${pageIndex}`}>
              {(page.data as EstablishmentCompanyData[]).map((establishment) => (
                <React.Fragment key={`establishment-card-${establishment.id}`}>
                  {establishment.general_information && (
                    <div
                      id={`establishment-card-${establishment.id}`}
                      className="w-[336px] flex-col bg-map-search-50 backdrop-blur rounded-md"
                    >
                      <EstablishmentTooltipDetails
                        cardList
                        establishment={establishment}
                        setFilterBounds={setFilterBounds}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}

          <div ref={observerRef} className="h-px bg-transparent text-center font-light italic">
            {isFetchingNextPage && 'Carregando...'}
            {!isFetchingNextPage && hasNextPage && 'Role para carregar mais'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstablishmentMapCardList;
